import { type FC, type ComponentProps, useCallback, type ReactNode } from 'react';
import { useFragment, graphql, usePaginationFragment } from 'react-relay';

import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    triggerConstants,
} from 'dibs-tracking';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes.ts';

import { HpSharedItemTilesCarouselModule } from '../HpSharedItemTilesCarouselModule/HpSharedItemTilesCarouselModule.tsx';

import { type HpSharedExcellentValuesCarousel_viewer$key } from './__generated__/HpSharedExcellentValuesCarousel_viewer.graphql.ts';
import { type HpSharedExcellentValuesCarousel_user$key } from './__generated__/HpSharedExcellentValuesCarousel_user.graphql.ts';

const viewerFragment = graphql`
    fragment HpSharedExcellentValuesCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedExcellentValuesCarouselPaginationQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        excellentValuesItems: itemSearchTrending(
            first: $count
            after: $cursor
            module: EXCELLENT_VALUES_MODULE
        ) @connection(key: "HpSharedExcellentValuesCarousel_excellentValuesItems", filters: []) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    ...HpSharedItemTilesCarouselModule_items
                        @arguments(
                            fetchFavorites: $hasUserId
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            totalResults
        }
        ...HpSharedItemTilesCarouselModule_viewer
            @arguments(fetchFavorites: $hasUserId, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment HpSharedExcellentValuesCarousel_user on User {
        ...HpSharedItemTilesCarouselModule_user
    }
`;

export const HpSharedExcellentValuesCarousel: FC<{
    viewer?: HpSharedExcellentValuesCarousel_viewer$key | null;
    user?: HpSharedExcellentValuesCarousel_user$key | null;
    isMobile: boolean;
    title?: ReactNode;
    viewMoreLink?: string | null;
    addTopGap?: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile, title, viewMoreLink, addTopGap }) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const onArrowClick: ComponentProps<typeof HpSharedItemTilesCarouselModule>['onArrowClick'] =
        useCallback(() => {
            trackEvent({
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_HOMEPAGE,
                step_interaction_name:
                    stepInteractionConstants.STEP_INTERACTION_EXCELLENT_VALUES_ARROW_CLICK,
                trigger: triggerConstants.TRIGGER_EXCELLENT_VALUES,
            });
        }, []);

    const { excellentValuesItems } = viewer || {};
    const items = flattenConnectionNodes(excellentValuesItems);
    const totalResults = excellentValuesItems?.totalResults || 0;

    return (
        <HpSharedItemTilesCarouselModule
            viewer={viewer}
            user={user}
            items={items}
            isMobile={isMobile}
            totalResults={totalResults}
            trackingList={triggerConstants.TRIGGER_EXCELLENT_VALUES}
            loadNext={loadNext}
            onArrowClick={onArrowClick}
            title={title}
            viewMoreLink={viewMoreLink}
            dataTn="excellent-values"
            addTopGap={addTopGap}
        />
    );
};
